import { useState, useCallback } from 'react';
import { useAuth } from '@modules/auth';
import {
  fetchQuotes,
  fetchQuote,
  fetchQuoteItems,
  createQuote,
  createQuoteVersion,
  updateQuote,
  upsertQuoteItems,
  copyQuoteAsBranch,
  deleteQuote,
  type QuoteCreateInput,
  type QuoteUpdateInput,
  type QuoteItemInput,
} from '../services/quote.service';
import type { Quote, QuoteItem, ID } from '@shared/types';

export interface QuoteFilters {
  customerId?: ID;
  orderId?: ID;
  assignedUserId?: ID;
  includeDisabled?: boolean;
}

export function useQuote() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadQuotes = useCallback(async (filters?: QuoteFilters) => {
    setLoading(true); setError(null);
    try { setQuotes(await fetchQuotes(filters)); }
    catch (e) { setError(e instanceof Error ? e.message : '見積の取得に失敗しました'); }
    finally { setLoading(false); }
  }, []);

  const loadQuote = useCallback(async (id: ID) => {
    setLoading(true); setError(null);
    try { setQuote(await fetchQuote(id)); }
    catch (e) { setError(e instanceof Error ? e.message : '見積の取得に失敗しました'); }
    finally { setLoading(false); }
  }, []);

  const loadItems = useCallback(async (quoteId: ID) => {
    try { setItems(await fetchQuoteItems(quoteId)); }
    catch (e) { setError(e instanceof Error ? e.message : '明細の取得に失敗しました'); }
  }, []);

  const addQuote = useCallback(async (data: QuoteCreateInput): Promise<Quote> => {
    if (!user) throw new Error('ログインが必要です');
    const created = await createQuote(data, user.id);
    setQuotes((prev) => [created, ...prev]);
    return created;
  }, [user]);

  const editQuote = useCallback(async (id: ID, data: QuoteUpdateInput): Promise<Quote> => {
    if (!user) throw new Error('ログインが必要です');
    const updated = await updateQuote(id, data, user.id);
    setQuotes((prev) => prev.map((q) => (q.id === id ? updated : q)));
    return updated;
  }, [user]);

  const createVersion = useCallback(async (originalId: ID): Promise<Quote> => {
    if (!user) throw new Error('ログインが必要です');
    const version = await createQuoteVersion(originalId, user.id);
    setQuotes((prev) => [version, ...prev]);
    return version;
  }, [user]);

  const upsertItems = useCallback(async (quoteId: ID, newItems: QuoteItemInput[]): Promise<QuoteItem[]> => {
    if (!user) throw new Error('ログインが必要です');
    const saved = await upsertQuoteItems(quoteId, newItems, user.id);
    setItems(saved);
    return saved;
  }, [user]);

  const removeQuote = useCallback(async (id: ID): Promise<void> => {
    if (!user) throw new Error('ログインが必要です');
    await deleteQuote(id, user.id);
    setQuotes((prev) => prev.filter((q) => q.id !== id));
  }, [user]);

  const copyAsBranch = useCallback(async (sourceId: ID): Promise<ID> => {
    if (!user) throw new Error('ログインが必要です');
    const { id: newId } = await copyQuoteAsBranch(sourceId, user.id);
    return newId;
  }, [user]);

  return {
    quotes, quote, items, loading, error,
    loadQuotes, loadQuote, loadItems,
    addQuote, editQuote, createVersion, upsertItems, copyAsBranch, removeQuote,
  };
}
